import { Component, useRef } from 'react';
import { Accordion, Grid, Icon, Segment } from 'semantic-ui-react';
import { useWikiSearch } from '../WikiSearchContext';
import useHighlightMatches from '../useHighlightMatches';

class PmGuideClass extends Component {
  state = { activeIndex: [0, 1, 2, 3, 4, 5] };

  handleClick = (e, titleProps) => {
    const { index } = titleProps;
    const { activeIndex } = this.state;

    const newIndex = activeIndex.includes(index)
      ? activeIndex.filter((i) => i !== index)
      : [...activeIndex, index];

    this.setState({ activeIndex: newIndex });
  };

  render() {
    const { activeIndex } = this.state;
    const { contentRef } = this.props;

    return (
      <div ref={contentRef}>
        <Grid id="detailed-segment">
          <Grid.Column width={16}>
            <Segment.Group>
              <Segment
                basic
                attached="top"
                style={{ padding: '0' }}
                id="segment"
              >
                <Accordion styled fluid id="segment" exclusive={false}>
                  <Accordion.Title
                    active={activeIndex.includes(0)}
                    index={0}
                    onClick={this.handleClick}
                    id="green"
                  >
                    <Icon name="dropdown" />
                    Welcome to React
                  </Accordion.Title>
                  <Accordion.Content active={activeIndex.includes(0)}>
                    <p>
                      The primary role of a Program Manager (PM) is providing
                      support to others. One of the reasons we started React was
                      because we saw so many great Program Managers, BHPs and
                      families go unsupported.
                    </p>
                    <p>
                      The purpose of this guide is to offer some documentation
                      on what has worked for us, as well as our vision and
                      expectations from you as a Program Manager. This Program
                      Manager guide is an extension to the Employee Handbook and
                      therefore some areas are not repeated.
                    </p>
                  </Accordion.Content>

                  <Accordion.Title
                    active={activeIndex.includes(1)}
                    index={1}
                    onClick={this.handleClick}
                    id="green"
                  >
                    <Icon name="dropdown" />
                    Our Vision of Service
                  </Accordion.Title>
                  <Accordion.Content active={activeIndex.includes(1)}>
                    <p>
                      Program Managers are involved throughout the entire
                      client-support cycle. PMs play an essential role as they
                      work to bridge the gap between clients, families, BHPs and
                      other members of the support team. PMs work closely with
                      our Program Development team in order to link client needs
                      with the best possible BHP. Our Program Development team
                      will need to know which clients & BHPs are thriving and
                      which may need additional support.
                    </p>
                    <p>
                      The Program Development team will provide a background of
                      each client after they have been onboarded. Our support
                      team will also provide backgrounds for BHPs in order to
                      better prepare you.
                    </p>
                    <p>
                      Before a BHP can begin offering services, they must first
                      be introduced to the family. This meet and greet will be
                      used to introduce both BHP and family members, as well as
                      reiterate the process with families. During this time it
                      is important to facilitate the meeting between family and
                      BHP so they both understand how to move forward. You may
                      need to clarify certain needs, goals, scenarios, etc with
                      all parities. The client's Individualized Treatment Plan
                      (ITP) should also be reviewed so all parties can clarify
                      any questions. This plan will have your client's goals
                      which can then be reviewed and discussed. Have the family
                      explain certain scenarios and why they feel these goals
                      are important to work on while also bringing the BHP into
                      the picture and having them discuss how they might start
                      working on each goal. Think back to your time as a BHP and
                      what information would of helped you. It's also important
                      to take good notes which can then be shared and utilized
                      by the rest of the support team.
                    </p>
                  </Accordion.Content>

                  <Accordion.Title
                    active={activeIndex.includes(2)}
                    index={2}
                    onClick={this.handleClick}
                    id="green"
                  >
                    <Icon name="dropdown" />
                    Our Platform
                  </Accordion.Title>
                  <Accordion.Content active={activeIndex.includes(2)}>
                    <p>
                      React differs from most other agencies in that we really
                      try to streamline our workflow using technology. We are
                      constantly looking for ways to make things more efficient,
                      as this will ultimately give everyone more time to focus
                      on providing the best care possible. This approach means
                      that we will be continually updating and changing our tech
                      stack, as we find new and improved ways to do things. It
                      will be important to keep an open mind and not become
                      entrenched in any one way of doing things.
                    </p>
                    <p>
                      As a PM, you will use our software application, the
                      react-portal in order to manage your case load. Your BHPs
                      will use the react-portal to document each Session which
                      you will then need review. One of our primary goals is to
                      provide consistent feedback, so these sessions will need
                      to be reviewed daily. Once a session has been reviewed,
                      you will add your initials to the Program Manager text
                      area input which will signify you have reviewed the entire
                      session note. We encourage you to provide daily feedback
                      and answer any questions that may arise in the 'Questions
                      & Feedback' section at the bottom of each session note. We
                      want the entire team to stay connected to how sessions are
                      going - and also offer that BHP the opportunity to reach
                      out for guidance and help. This documentation will be more
                      usable and accessible to everyone, so we also want to
                      encourage parents to provide feedback as well.
                    </p>
                    <p>
                      The react-portal will also be used for Plans, and
                      Supervisions, which are the meetings you have with BHPs.
                      As a PM, you will also be responsible for facilitating the
                      mandated meetings with families, as well as the weekly
                      supervisions with BHPs.
                    </p>
                    <p>
                      The other primary hub we use is Google's G-Suite. This
                      includes Google Docs, Google Sheets, Google Drive, etc. It
                      will be crucial to maintain a continually updated
                      spreadsheet of family meeting due-dates. A complete list
                      of applications currently used can be view at -{' '}
                      <a href="https://docs.google.com/spreadsheets/d/1zkTfwsxFTW0qW_z4WD-HdVNi37iuVO80Q05AfqulHP8/edit#gid=1967649711">
                        Directory - Phone & Apps
                      </a>
                    </p>
                    <p>
                      It will also be important to monitor and update
                      spreadsheets such as -{' '}
                      <a href="https://docs.google.com/spreadsheets/d/1aKrm361RBlJII8QHEGqh6OJBFRxCI3X_wlpADroJKew/edit#gid=0">
                        Client/Staff List
                      </a>{' '}
                      and
                      <a href="https://docs.google.com/spreadsheets/d/16o6lHDumIbGmqvvVSHsXe98gz1QcoRPf5x8nnYgMhgY/edit#gid=1751423575">
                        {' '}
                        Weekly Hours
                      </a>
                    </p>
                  </Accordion.Content>

                  <Accordion.Title
                    active={activeIndex === 3}
                    index={3}
                    onClick={this.handleClick}
                    id="green"
                  >
                    <Icon name="dropdown" />
                    Goals for Program Managers
                  </Accordion.Title>
                  <Accordion.Content active={activeIndex === 3}>
                    <ul>
                      <li id="orange">Maximize client progression</li>
                      <li id="orange">Support BHPs and families</li>
                      <li id="orange">
                        Track and sign off on daily portal sessions
                      </li>
                      <li id="orange">
                        Schedule and facilitate Meet & Greets, family
                        supervisions, BHP Supervisions, etc
                      </li>
                      <li id="orange">
                        Track and conduct 90 Day Reviews, CSRs, monthly in-home
                        meetings, etc
                      </li>
                      <li id="orange">
                        Provide feedback on ITPs, client progression, BHPs, etc
                      </li>
                      <li id="orange">
                        Attend Group Supervisions and other trainings
                      </li>
                    </ul>
                  </Accordion.Content>

                  <Accordion.Title
                    active={activeIndex === 4}
                    index={4}
                    onClick={this.handleClick}
                    id="green"
                  >
                    <Icon name="dropdown" />
                    Attendance at Work
                  </Accordion.Title>
                  <Accordion.Content active={activeIndex === 4}>
                    <p>
                      PMs are expected to manage their own time and be
                      self-driven. You will not have set hours, but you should
                      expect to be present in the office for around half of your
                      40 hour work week. The other half of your week will be
                      focused on meetings such as meet & greets and monthly
                      supervisions.
                    </p>
                    <p>
                      It is your responsibility to maintain consistent
                      communication with your families, BHPs and team-members.
                      Communication will occur in-person, over the phone,
                      text-message and through the company Slack chat.
                    </p>
                  </Accordion.Content>

                  <Accordion.Title
                    active={activeIndex === 5}
                    index={5}
                    onClick={this.handleClick}
                    id="green"
                  >
                    <Icon name="dropdown" />
                    Performance Expecations and Evaluation
                  </Accordion.Title>
                  <Accordion.Content active={activeIndex === 5}>
                    <h5>
                      Performance Development Planning and Feedback Process
                    </h5>
                    <p>
                      Written performance evaluations will be prepared for each
                      employee after three months of employment and then on an
                      annual basis. After three months, if the employee has
                      demonstrated the duties of the position to be performed
                      satisfactorily, they will earn PTO time as well as come
                      off the initial probationary period. Program Managers will
                      be responsible for conducting employee reviews for each
                      BHP.
                    </p>
                    <p>
                      Annual reviews are to document and maintain satisfactory
                      performance of all employees. Reviews are also a means of
                      measuring an employee’s effectiveness as well as
                      identifying areas where an employee is in need of training
                      or improving.
                    </p>
                  </Accordion.Content>
                </Accordion>
              </Segment>
            </Segment.Group>
          </Grid.Column>
        </Grid>
      </div>
    );
  }
}

const PmGuide = (props) => {
  const { searchTerm } = useWikiSearch();
  const contentRef = useRef(null);
  useHighlightMatches(contentRef);

  return (
    <PmGuideClass {...props} searchTerm={searchTerm} contentRef={contentRef} />
  );
};

export default PmGuide;
